import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import SEO from '../components/SEO';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  ArrowSquareOut,
  Briefcase,
  Buildings,
  CaretDown,
  CaretUp,
  CircleNotch,
  Envelope,
  Funnel,
  Globe,
  MagnifyingGlass,
  MapPin,
  UsersThree,
  X
} from '@phosphor-icons/react';
const PartnerDirectory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [options, setOptions] = useState({
    service_locations: [],
    industries: [],
    services: []
  });
  
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    industry: '',
    service: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [expandedPartner, setExpandedPartner] = useState(null);

  useEffect(() => {
    fetchOptions();
    fetchPartners();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPartners();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/partners/options/all`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.country) params.append('country', filters.country);
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.service) params.append('service', filters.service);

      const response = await fetch(`${BACKEND_URL}/api/partners?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      country: '',
      industry: '',
      service: ''
    });
  };

  const hasActiveFilters = filters.country || filters.industry || filters.service;

  return (
    <>
      <SEO 
        title="Find a Partner - Man & Machine" 
        description="Find certified AI consultants, agencies, and automation professionals to help you design, deploy, and manage AI solutions."
      />
      <PublicNavHeader />
      
      <div className="min-h-screen bg-[#0a0a0f]">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/10 via-transparent to-[#EC4899]/10" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#6366F1]/20 blur-[120px] rounded-full" />
          
          <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] text-sm font-medium mb-6">
                <UsersThree size={16} />
                Partner Directory
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                Find a{' '}
                <span className="bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-transparent bg-clip-text">
                  Partner
                </span>
              </h1>
              
              <p className="text-lg text-[#A0AEC0] mb-8">
                Connect with certified AI consultants, agencies, and automation professionals who can help you design, deploy, and manage AI solutions.
              </p>

              {/* MagnifyingGlass Bar */}
              <div className="relative max-w-xl mx-auto">
                <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search partners by name or description..."
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filters & Results */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          {/* Funnel Bar */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center gap-4 mb-8"
          >
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                showFilters || hasActiveFilters
                  ? 'bg-[#6366F1] text-white'
                  : 'glass text-[#A0AEC0] hover:text-white'
              }`}
            >
              <Funnel size={16} />
              Filters
              {hasActiveFilters && (
                <span className="w-5 h-5 rounded-full bg-white text-[#6366F1] text-xs flex items-center justify-center font-bold">
                  {[filters.country, filters.industry, filters.service].filter(Boolean).length}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <X size={14} />
                Clear all
              </button>
            )}

            <div className="ml-auto text-sm text-[#A0AEC0]">
              {total} partner{total !== 1 ? 's' : ''} found
            </div>
          </motion.div>

          {/* Expanded Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="glass rounded-2xl p-6 mb-8"
            >
              <div className="grid sm:grid-cols-3 gap-4">
                {/* Country Funnel */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Country</label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <select
                      value={filters.country}
                      onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none appearance-none cursor-pointer"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="">All Countries</option>
                      {options.service_locations.filter(loc => loc !== 'Global (Remote)').map((loc) => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                    <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                </div>

                {/* Industry Funnel */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Industry</label>
                  <div className="relative">
                    <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <select
                      value={filters.industry}
                      onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none appearance-none cursor-pointer"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="">All Industries</option>
                      {options.industries.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                    <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                </div>

                {/* Service Funnel */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Service</label>
                  <div className="relative">
                    <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <select
                      value={filters.service}
                      onChange={(e) => setFilters({ ...filters, service: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:border-[#6366F1] focus:outline-none appearance-none cursor-pointer"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="">All Services</option>
                      {options.services.map((svc) => (
                        <option key={svc} value={svc}>{svc}</option>
                      ))}
                    </select>
                    <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Partners Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <CircleNotch size={40} className="text-[#6366F1] animate-spin" />
            </div>
          ) : partners.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="w-20 h-20 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-4">
                <UsersThree size={40} className="text-[#6366F1]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Partner listing coming soon</h3>
              <p className="text-[#A0AEC0] mb-6">
                {hasActiveFilters 
                  ? 'Try adjusting your filters to find more partners.'
                  : 'Check back soon as we add partners to our directory.'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-6 py-2.5 rounded-xl bg-[#6366F1] text-white font-medium hover:opacity-90 transition-opacity"
                >
                  Clear Filters
                </button>
              )}
            </motion.div>
          ) : (
            <div className="space-y-4">
              {partners.map((partner, index) => (
                <motion.div
                  key={partner.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass rounded-2xl overflow-hidden"
                >
                  {/* Partner Row - Always Visible */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                    onClick={() => setExpandedPartner(expandedPartner === partner.id ? null : partner.id)}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      {/* Name & Location */}
                      <div className="lg:w-1/4">
                        <h3 className="text-lg font-semibold text-white">
                          {partner.display_name || partner.business_name}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-[#A0AEC0] mt-1">
                          <MapPin size={14} />
                          {partner.city}, {partner.country}
                        </div>
                      </div>

                      {/* Description Preview - Truncated */}
                      <div className="lg:flex-1">
                        <p className="text-sm text-[#A0AEC0] line-clamp-2">
                          {partner.about_business || partner.short_description}
                        </p>
                      </div>

                      {/* Services Preview */}
                      <div className="hidden md:flex flex-wrap gap-1.5 lg:w-1/4">
                        {partner.services_offered?.slice(0, 2).map((service) => (
                          <span
                            key={service}
                            className="px-2 py-1 text-xs rounded-md bg-[rgba(255,255,255,0.05)] text-[#A0AEC0]"
                          >
                            {service}
                          </span>
                        ))}
                        {partner.services_offered?.length > 2 && (
                          <span className="px-2 py-1 text-xs rounded-md bg-[rgba(255,255,255,0.05)] text-[#A0AEC0]">
                            +{partner.services_offered.length - 2}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3">
                        <a
                          href={`mailto:${partner.contact_email || ''}?subject=Inquiry from Man & Machine Partner Directory`}
                          onClick={(e) => e.stopPropagation()}
                          className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] text-white text-sm font-medium hover:opacity-90 transition-opacity"
                        >
                          <Envelope size={14} />
                          Contact
                        </a>
                        <button className="p-2 rounded-lg bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:text-white transition-colors">
                          {expandedPartner === partner.id ? <CaretUp size={20} /> : <CaretDown size={20} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedPartner === partner.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-2 border-t border-[rgba(255,255,255,0.05)]">
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Full Description */}
                            <div className="lg:col-span-3">
                              <h4 className="text-sm font-medium text-white mb-2">About</h4>
                              <p className="text-sm text-[#A0AEC0] leading-relaxed whitespace-pre-wrap">
                                {partner.about_business || partner.short_description}
                              </p>
                            </div>

                            {/* Contact Info */}
                            <div>
                              <h4 className="text-sm font-medium text-white mb-2">Contact</h4>
                              <div className="space-y-2">
                                <a
                                  href={`mailto:${partner.contact_email || ''}?subject=Inquiry from Man & Machine Partner Directory`}
                                  className="flex items-center gap-2 text-sm text-[#6366F1] hover:underline"
                                >
                                  <Envelope size={14} />
                                  {partner.contact_email}
                                </a>
                                {partner.website && (
                                  <a
                                    href={partner.website.startsWith('http') ? partner.website : `https://${partner.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm text-[#6366F1] hover:underline"
                                  >
                                    <ArrowSquareOut size={14} />
                                    Visit Website
                                  </a>
                                )}
                              </div>
                            </div>

                            {/* All Services */}
                            <div>
                              <h4 className="text-sm font-medium text-white mb-2">Services Offered</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {partner.services_offered?.map((service) => (
                                  <span
                                    key={service}
                                    className="px-2 py-1 text-xs rounded-md bg-[rgba(255,255,255,0.05)] text-[#A0AEC0]"
                                  >
                                    {service}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* All Industries */}
                            <div>
                              <h4 className="text-sm font-medium text-white mb-2">Industries Served</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {partner.industries_served?.map((industry) => (
                                  <span
                                    key={industry}
                                    className="px-2 py-1 text-xs rounded-md bg-[#6366F1]/10 text-[#6366F1]"
                                  >
                                    {industry}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Service Locations */}
                            <div className="lg:col-span-3">
                              <h4 className="text-sm font-medium text-white mb-2">Service Locations</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {partner.service_locations?.map((loc) => (
                                  <span
                                    key={loc}
                                    className="px-2 py-1 text-xs rounded-md bg-[#14F195]/10 text-[#14F195]"
                                  >
                                    {loc}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Mobile Contact Button */}
                          <div className="mt-6 sm:hidden">
                            <a
                              href={`mailto:${partner.contact_email || ''}?subject=Inquiry from Man & Machine Partner Directory`}
                              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#6366F1] text-white font-medium hover:opacity-90 transition-opacity"
                            >
                              <Envelope size={16} />
                              Contact Partner
                            </a>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 text-center"
          >
            <div className="glass rounded-2xl p-8 max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-3">
                Become a Partner
              </h2>
              <p className="text-[#A0AEC0] mb-6">
                Are you an AI consultant, agency, or automation professional? Join our partner network and get listed in our directory.
              </p>
              <Link
                to="/partners/apply"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
              >
                Apply to Become a Partner
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default PartnerDirectory;
